"use client";

import { Skeleton, Paper, Grid } from "@forestsync/ui";

interface TokenSkeletonProps {
  count?: number;
}

export function TokenSkeleton({ count = 8 }: TokenSkeletonProps) {
  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className="border border-gray-200 dark:border-gray-700 rounded-md shadow-sm transition-all duration-200 ease-in-out px-5 py-4 flex flex-col gap-3 bg-white dark:bg-gray-800 animate-in fade-in duration-300 fill-mode-both"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className="flex items-center gap-2">
              <Skeleton height={16} width={120} radius="sm" />
            </div>
            <div className="flex items-center gap-1">
              <Skeleton height={14} width={60} className="mr-2" />
              <Skeleton height={14} width={80} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MapSkeleton() {
  return <Skeleton height="100vh" radius={0} />;
}

export function SinkDetailSkeleton() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton height={40} width="60%" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Skeleton height={200} />
          <Skeleton height={300} />
        </div>
        <div>
          <Skeleton height={400} />
        </div>
      </div>
    </div>
  );
}

export function EventsTokensSkeleton() {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="space-y-4">
        {/* Tabs skeleton */}
        <div className="flex space-x-4">
          <Skeleton height={40} width={100} />
          <Skeleton height={40} width={100} />
        </div>

        {/* Content area skeleton */}
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton height={50} />
              {i === 0 && (
                <div className="pl-4 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {Array.from({ length: 4 }, (_, j) => (
                      <div
                        key={j}
                        className="border border-gray-200 dark:border-gray-700 rounded-md shadow-sm px-5 py-4 flex flex-col gap-3 bg-white dark:bg-gray-800"
                      >
                        <Skeleton height={16} width={120} radius="sm" />
                        <div className="flex items-center gap-1">
                          <Skeleton height={14} width={60} className="mr-2" />
                          <Skeleton height={14} width={80} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function UserSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header: icon, name, refId */}
      <div className="flex items-center gap-4">
        <Skeleton height={48} width={48} radius="xl" />
        <Skeleton height={32} width={200} />
        <Skeleton height={24} width={100} />
      </div>
      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton height={16} width={80} />
            <Skeleton height={24} width={60} />
          </div>
        ))}
      </div>
      {/* Main content: overview card and contact card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Overview card */}
          <Skeleton height={180} />
        </div>
        <div>
          {/* Contact card */}
          <Skeleton height={180} />
        </div>
      </div>
      {/* Tabs and table placeholder */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex space-x-4 mb-4">
          <Skeleton height={40} width={120} />
          <Skeleton height={40} width={120} />
          <Skeleton height={40} width={120} />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={24} width={200} />
              <Skeleton height={24} width={120} />
              <Skeleton height={24} width={80} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Update PassportSkeleton to match the current token passport layout
export function PassportSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header: icon, serial, status, refId */}
      <div className="flex items-center gap-4">
        <Skeleton height={48} width={48} radius="xl" />
        <Skeleton height={32} width={220} />
        <Skeleton height={24} width={100} />
        <Skeleton height={24} width={80} />
      </div>
      {/* Token info grid */}
      <Paper withBorder p="lg" radius="md" className="mt-4">
        <Grid>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6, md: 4 }}>
              <Skeleton height={24} width="80%" radius="sm" />
              <Skeleton height={16} width="60%" radius="sm" className="mt-2" />
            </Grid.Col>
          ))}
        </Grid>
      </Paper>
      {/* Table placeholder for token events/history */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-4">
        <div className="flex space-x-4 mb-4">
          <Skeleton height={32} width={100} />
          <Skeleton height={32} width={100} />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={20} width={180} />
              <Skeleton height={20} width={100} />
              <Skeleton height={20} width={60} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
